import {
  MessageType,
  Sender,
  Body as MessageBody,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import {
  isList,
  isLiteral,
  isNonNullable,
  isNumber,
  isRecord,
  isString,
  isUndefined,
} from '@/utils';

export * as WidgetApi from '@wix/ambassador-innovation-widget-v1-message/types';

type NonEmptyArray<T> = [T, ...T[]];

export interface Message {
  id: string;
  createdDate?: number;
  sender?: Sender;
  conversationId?: string | null;
  answerTo?: string | null;
  triggerId?: string | null;
  messageType?: MessageType;
  body?: MessageBody;
  inboxId?: string | null;
  metaSiteId?: string | null;
  inboxConversationId?: string | null;
  visitorOptions?: string[];
  assistantActions?: string[];
  visitorOptionsFields?: VisitorOptionsField[];
}

export type VisitorOptionsField =
  | TextField
  | NumberField
  | DatetimeField
  | SelectionField
  | ColorField;

export const enum AssistantAction {
  AddToCurrentCart = 'AddToCurrentCart',
  UpdateCurrentCart = 'UpdateCurrentCart',
  RemoveLineItemsFromCurrentCart = 'RemoveLineItemsFromCurrentCart',
  UpdateCurrentCartLineItemQuantity = 'UpdateCurrentCartLineItemQuantity',
}

export interface MessageWithVisitorOptions extends Message {
  visitorOptions: NonEmptyArray<string>;
}

export const hasVisitorOptions = (
  message: Message,
): message is MessageWithVisitorOptions =>
  (message.visitorOptions?.length ?? 0) > 0;

export interface MessageWithAssistantActions extends Message {
  assistantActions: NonEmptyArray<string>;
}

export const hasAssistantActions = (
  message: Message,
): message is MessageWithAssistantActions =>
  (message.assistantActions?.length ?? 0) > 0;

export interface MessageWithBody extends Message {
  body: MessageBodyWithText;
}

export interface MessageBodyWithText extends MessageBody {
  mainText: string;
}

export const hasBodyWithText = (message: Message): message is MessageWithBody =>
  !!message.body &&
  typeof message.body.mainText === 'string' &&
  message.body.mainText.trim().length > 0;

export interface MessageWithVisitorOptionsFields extends Message {
  visitorOptionsFields: VisitorOptionsField[];
}

export const hasVisitorOptionsFields = (
  message: Message,
): message is MessageWithVisitorOptionsFields =>
  (message.visitorOptionsFields?.length ?? 0) > 0;

export const enum VisitorOptionsFieldTypes {
  Number = 'number',
  Text = 'text',
  Selection = 'drop_down',
  Color = 'color',
  Datetime = 'datetime',
}

export interface TextField {
  type: VisitorOptionsFieldTypes.Text;
  name?: string;
  max?: number;
}

export interface NumberField {
  type: VisitorOptionsFieldTypes.Number;
  name?: string;
  min?: number;
  max?: number;
}

export interface DatetimeField {
  type: VisitorOptionsFieldTypes.Datetime;
  name?: string;
  description?: string;
}

export interface SelectionField {
  type: VisitorOptionsFieldTypes.Selection;
  name?: string;
  choices: SelectionChoice[];
}
export interface SelectionChoice {
  choice: string;
  description?: string;
}

export interface ColorField {
  type: VisitorOptionsFieldTypes.Color;
  name?: string;
  choices: ColorChoice[];
}

export interface ColorChoice {
  choice: string;
  description?: string;
}

export const parseNumberField = (from: unknown): NumberField | undefined => {
  if (
    isRecord(from) &&
    isLiteral(from.type, VisitorOptionsFieldTypes.Number) &&
    (isString(from.name) || isUndefined(from.name)) &&
    (isNumber(from.min) || isUndefined(from.min)) &&
    (isNumber(from.max) || isUndefined(from.max))
  ) {
    return {
      type: from.type,
      name: from.name,
      min: from.min,
      max: from.max,
    };
  }
};

export const parseTextField = (from: unknown): TextField | undefined => {
  if (
    isRecord(from) &&
    isLiteral(from.type, VisitorOptionsFieldTypes.Text) &&
    (isString(from.name) || isUndefined(from.name)) &&
    (isNumber(from.max) || isUndefined(from.max))
  ) {
    return {
      type: from.type,
      name: from.name,
      max: from.max,
    };
  }
};

export const parseSelectionField = (
  from: unknown,
): SelectionField | undefined => {
  if (
    isRecord(from) &&
    isLiteral(from.type, VisitorOptionsFieldTypes.Selection) &&
    (isString(from.name) || isUndefined(from.name)) &&
    isList(from.choices)
  ) {
    return {
      type: from.type,
      name: from.name,
      choices: from.choices.map(parseSelectionChoice).filter(isNonNullable),
    };
  }
};

export const parseSelectionChoice = (
  from: unknown,
): SelectionChoice | undefined => {
  if (
    isRecord(from) &&
    isString(from.choice) &&
    (isString(from.description) || isUndefined(from.description))
  ) {
    return {
      choice: from.choice,
      description: from.description,
    };
  }
};

export const parseColorField = (from: unknown): ColorField | undefined => {
  if (
    isRecord(from) &&
    isLiteral(from.type, VisitorOptionsFieldTypes.Color) &&
    (isString(from.name) || isUndefined(from.name)) &&
    isList(from.choices)
  ) {
    return {
      type: from.type,
      name: from.name,
      choices: from.choices.map(parseColorChoice).filter(isNonNullable),
    };
  }
};

export const parseColorChoice = (from: unknown): ColorChoice | undefined => {
  if (
    isRecord(from) &&
    isString(from.choice) &&
    (isString(from.description) || isUndefined(from.description))
  ) {
    return {
      choice: from.choice,
      description: from.description,
    };
  }
};

export const parseDatetimeField = (
  from: unknown,
): DatetimeField | undefined => {
  if (
    isRecord(from) &&
    isLiteral(from.type, VisitorOptionsFieldTypes.Datetime) &&
    (isString(from.name) || isUndefined(from.name)) &&
    (isString(from.description) || isUndefined(from.description))
  ) {
    return {
      type: from.type,
      name: from.name,
      description: from.description,
    };
  }
};
